import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop, routes } from '@blockpulse3/data/shared';
import {
  CompanyDocumentType,
  CreateUboInput,
  DocumentStatus,
  IdentityVerificationStatus,
  UboRole,
  UpdateUboInput,
  useCreateUboMutation,
  useDeleteCompanyDocumentMutation,
  useDeleteUboMutation,
  useGetCompanyQuery,
  useGetDocumentPdfUrlLazyQuery,
  useGetPappersExtractInpiDocumentQuery,
  useSearchSirenLazyQuery,
  useUpdateUboMutation,
} from '@blockpulse3/graphql/hooks';
import { isCompanyComplete } from '@blockpulse3/helpers';
import {
  CONTENT_LOADING,
  DropzoneInput,
  ErrorQueryCard,
  PreviewDocumentModal,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { useStepFormContext } from '../../../provider';
import { CompanyOwnerForm } from './CompanyOwnerForm';
import { OwnerForm } from './types';
import { getCompanyOwners, getPappersOwners } from './utils';

type Props = {
  /* ** Is editing mode ** */
  isEditing?: boolean;
  /* ** Step cancelled callback ** */
  onCancel?: () => void;
  /* ** Step submitted callback ** */
  onSubmit?: () => void;
};

export function CompanyOwners({
  isEditing = false,
  onCancel = noop,
  onSubmit = noop,
}: Props): JSX.Element {
  const i18nOwners = useTranslations('OwnersForm');
  const i18nGeneralToastDocuments = useTranslations('General.Toast.Documents');

  const [dropzoneFileName, setDropzoneFileName] = useState<{ [key: string]: File }>({});

  const [isPollingStarted, setIsPollingStarted] = useState<boolean>(false);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [documentPdfUrl, setDocumentPdfUrl] = useState<string | symbol | null>(CONTENT_LOADING);

  const navigate = useNavigate();
  const { user } = useUser();
  const { companyId = '' } = useParams();

  const previewModal = useDisclosure();
  const { isOpen, onToggle, onClose } = useDisclosure();

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const [searchSiren, { data: pappersData, loading: pappersLoading }] = useSearchSirenLazyQuery();
  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();
  const [deleteCompanyDocument] = useDeleteCompanyDocumentMutation();

  const [createOwner] = useCreateUboMutation();
  const [updateOwner] = useUpdateUboMutation();
  const [deleteOwner] = useDeleteUboMutation();

  const [hiddenPappersOwners, setHiddenPappersOwners] = useState<string[]>([]);

  const { data, loading, error, refetch } = useGetCompanyQuery({
    variables: { companyId },
    onCompleted: (data) => {
      if (
        !isEditing &&
        data.company.kybVerificationStatus === IdentityVerificationStatus.APPROVED &&
        isCompanyComplete(data.company)
      ) {
        navigate(routes.workspace.fmt(data.company.id));
        return;
      }

      // Auto-fill company data if not already filled
      if (!data.company?.ubos || data.company.ubos.length === 0) {
        const siren = data.company.registrationNumber || '';
        if (siren) {
          searchSiren({ variables: { siren } });
        }
      }
    },
  });

  const getPappersDocReq = useGetPappersExtractInpiDocumentQuery({
    variables: { companyId },
    fetchPolicy: 'network-only',
    skip: !companyId,
  });
  const {
    data: extractInpiData,
    loading: extractInpiLoading,
    refetch: refetchExtractInpi,
  } = getPappersDocReq;

  useEffect(() => {
    if (
      extractInpiData?.getPappersExtractINPIDocument?.document &&
      extractInpiData.getPappersExtractINPIDocument.document.status === DocumentStatus.SIGNED
    ) {
      if (isPollingStarted) {
        getPappersDocReq.stopPolling();
        setIsPollingStarted(false);
      }
      setDropzoneFileName({
        [extractInpiData.getPappersExtractINPIDocument.document.id]: new File(
          [extractInpiData.getPappersExtractINPIDocument.document.title],
          extractInpiData.getPappersExtractINPIDocument.document.title,
        ),
      });
    }
  }, [extractInpiData, getPappersDocReq, isPollingStarted]);

  useEffect(() => {
    if (!getPappersDocReq) return;

    if (
      !isPollingStarted &&
      !isFileLoading &&
      extractInpiData?.getPappersExtractINPIDocument?.document?.status === DocumentStatus.ONGOING
    ) {
      // Start polling on INPI extract if not completely downloaded from Pappers
      setIsPollingStarted(true);
      getPappersDocReq.startPolling(1000);

      setTimeout(() => {
        getPappersDocReq.stopPolling();
        setIsPollingStarted(false);
      }, 20_000);
    }
  }, [isPollingStarted, getPappersDocReq, extractInpiData, isFileLoading]);

  /* ** Existing company owners ** */
  const companyOwners = useMemo(() => getCompanyOwners(data?.company), [data?.company]);
  /* ** Filtered Pappers owners ** */
  const pappersOwners = useMemo(
    () =>
      getPappersOwners(pappersData?.searchSIREN).filter(
        (owner) => !hiddenPappersOwners.includes(owner.id),
      ),
    [pappersData?.searchSIREN, hiddenPappersOwners],
  );
  /* ** Merge owners ** */
  const owners = [...companyOwners, ...pappersOwners];
  /* ** Look for user owner in list ** */
  const isUserSwitchDisabled = owners.find(
    (owner) => owner.individualIdentityId === user?.individualIdentity?.id,
  );
  /* ** Compute remaining sharePercentage, to block user on submit ** */
  const maxSharePercentage = 100 - companyOwners.reduce((acc, cur) => cur.sharePercentage + acc, 0);

  const handleNewOwnerForm = (): void => {
    onToggle();
  };

  const handleCancelOwnerForm = (): void => {
    onClose();
  };

  const handleHideOwnerForm = (id: string): void => {
    setHiddenPappersOwners((prev) => [...prev, id]);
  };

  const handleCreateOwner = (data: OwnerForm): void => {
    const hasCapital = data.role !== UboRole.LegalRepresentative;

    const createUboInput: CreateUboInput = {
      firstName: data.firstName,
      lastName: data.lastName,
      birthplace: data.birthplace,
      birthCityPostalCode: data.birthCityPostalCode,
      birthCountry: data.birthCountry.value,
      birthdate: data.birthdate,
      role: data.role,
      companyId,
      sharePercentage: hasCapital ? data.sharePercentage : 0,
      isDirect: hasCapital ? data.isDirect : false,
      individualIdentityId: data.individualIdentityId,
      address: {
        ...data.address,
        country: data.address.country.value,
      },
    };
    createOwner({
      variables: { createUboInput },
      onCompleted: () => {
        onClose();
        refetch();
        /* ** Hide form if data.id is specified ie. Pappers form ** */
        handleHideOwnerForm(data.id);
        successToast({ title: i18nOwners('Toast.CreateSuccess') });
      },
      onError: () => {
        errorToast({ title: i18nOwners('Toast.CreateError') });
      },
    });
  };

  const handleUpdateOwner = (data: OwnerForm): void => {
    const hasCapital = data.role !== UboRole.LegalRepresentative;

    const updateUboInput: UpdateUboInput = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      birthplace: data.birthplace,
      birthCityPostalCode: data.birthCityPostalCode,
      birthCountry: data.birthCountry.value,
      birthdate: data.birthdate,
      role: data.role,
      companyId,
      sharePercentage: hasCapital ? data.sharePercentage : 0,
      isDirect: hasCapital ? data.isDirect : false,
      individualIdentityId: data.individualIdentityId,
      address: {
        ...data.address,
        country: data.address.country.value,
      },
    };

    updateOwner({
      variables: { updateUboInput },
      onCompleted: () => {
        onClose();
        refetch();
        successToast({ title: i18nOwners('Toast.UpdateSuccess') });
      },
      onError: () => {
        errorToast({ title: i18nOwners('Toast.UpdateError') });
      },
    });
  };

  const handleDeleteOwner = (id: OwnerForm['id']): void => {
    if (!id) {
      return;
    }
    deleteOwner({
      variables: { deleteUboInput: { companyId, uboId: id } },
      onCompleted: () => {
        refetch();
        successToast({ title: i18nOwners('Toast.DeleteSuccess') });
      },
      onError: () => {
        errorToast({ title: i18nOwners('Toast.DeleteError') });
      },
    });
  };

  const { setCancelHandler, setSubmitHandler } = useStepFormContext();

  const handleStepSubmit = useCallback((): void => {
    if (companyOwners.length === 0) {
      errorToast({ title: i18nOwners('Toast.OwnerMinimum') });
    } else {
      onSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOwners.length, onSubmit]);

  const handleStepCancel = useCallback((): void => {
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    setSubmitHandler(handleStepSubmit);
    setCancelHandler(handleStepCancel);
  }, [handleStepSubmit, handleStepCancel, setSubmitHandler, setCancelHandler]);

  const handleFileDelete = (): void => {
    if (!companyId) return;

    deleteCompanyDocument({
      variables: {
        deleteCompanyDocumentInput: {
          companyId,
          documentType: CompanyDocumentType.DBE,
        },
      },
      onCompleted: () => {
        setDocumentPdfUrl(null);
        setDropzoneFileName({});
        successToast({ title: i18nGeneralToastDocuments('DeleteSuccess') });
      },
      onError: () => {
        errorToast({ title: i18nGeneralToastDocuments('DeleteError') });
      },
    });
  };

  const handleFileUpload = async (acceptedFile: File): Promise<void> => {
    if (!companyId) return;

    setIsFileLoading(true);
    const formData = new FormData();
    formData.append('companyId', companyId);
    formData.append('documentType', CompanyDocumentType.DBE);
    formData.append('document', acceptedFile);
    await axios.post(
      process.env['NX_API_CONTROLLER_ENDPOINT'] + '/companies/uploadCompanyDocument',
      formData,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    await refetchExtractInpi();
    setIsFileLoading(false);
  };

  const handleFileDrop = (acceptedFiles: File[]): void => {
    if (acceptedFiles[0]) {
      handleFileUpload(acceptedFiles[0]);
    }
  };

  const handleFilePreview = async (): Promise<void> => {
    if (!companyId) return;
    setDocumentPdfUrl(CONTENT_LOADING);
    previewModal.onOpen();

    if (extractInpiData?.getPappersExtractINPIDocument?.document) {
      getDocumentPdfUrl({
        variables: {
          documentId: extractInpiData.getPappersExtractINPIDocument.document.id,
        },
        fetchPolicy: 'no-cache',
        onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
          setDocumentPdfUrl(pdfUrl);
        },
      });
    }
  };

  if (loading || pappersLoading) {
    return <Skeleton height="350px" width="full" />;
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  if (!data) {
    return <ErrorQueryCard />;
  }

  const companyName = data.company.name;

  const disableAccordion =
    data.company.kybVerificationStatus === IdentityVerificationStatus.APPROVED;

  const sx = disableAccordion
    ? {
        opacity: 0.6,
        cursor: 'not-allowed',
      }
    : {};

  return (
    <>
      <Stack>
        <Text fontWeight={500}>{i18nOwners('DropzoneInputLabel')}</Text>
        <DropzoneInput
          isPreviewEnabled
          accept={{ 'application/pdf': [] }}
          files={dropzoneFileName}
          icon={<Icon as={DocumentTextIcon} boxSize="42px" color="gray.400" strokeWidth="1px" />}
          isLoading={extractInpiLoading || isPollingStarted || isFileLoading}
          maxFiles={1}
          subTitle=".pdf"
          onDelete={handleFileDelete}
          onDrop={handleFileDrop}
          onPreview={handleFilePreview}
        />
      </Stack>
      {pappersOwners.length > 0 && (
        <Alert flexShrink="0" status="info" sx={sx}>
          <AlertIcon />
          <Stack spacing="1">
            <AlertTitle>{i18nOwners('PappersAlertTitle', { companyName })}</AlertTitle>
          </Stack>
        </Alert>
      )}
      {companyOwners.map((owner) => (
        <Accordion key={owner.id} allowToggle index={disableAccordion ? -1 : undefined} sx={sx}>
          <AccordionItem mt="0">
            <AccordionButton>
              <Tooltip hasArrow label={i18nOwners('Tooltip.Success')} placement="top">
                <Icon as={CheckCircleIcon} boxSize="30" color="green.500" />
              </Tooltip>
              <Box flex="1" p="2" textAlign="left">
                <Heading fontSize="lg">{i18nOwners('OwnerTitle')}</Heading>
                <Text>
                  {owner.firstName} {owner.lastName}
                </Text>
              </Box>
              <AccordionIcon boxSize="8" />
            </AccordionButton>
            <AccordionPanel>
              <CompanyOwnerForm
                defaultValues={owner}
                isUserSwitchDisabled={true}
                maxSharePercentage={maxSharePercentage + owner.sharePercentage}
                onCancel={(): void => handleDeleteOwner(owner.id)}
                onSubmit={handleUpdateOwner}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ))}
      {pappersOwners.map((owner) => (
        <Accordion key={owner.id} allowToggle index={disableAccordion ? -1 : undefined} sx={sx}>
          <AccordionItem mt="0">
            <AccordionButton>
              <Tooltip hasArrow label={i18nOwners('Tooltip.Warning')} placement="top">
                <Icon as={InformationCircleIcon} boxSize="30" color="blue.400" />
              </Tooltip>
              <Box flex="1" p="2" textAlign="left">
                <Heading fontSize="lg">{i18nOwners('PappersOwnerTitle')}</Heading>
                <Text>
                  {owner.firstName} {owner.lastName}
                </Text>
              </Box>
              <AccordionIcon boxSize="8" />
            </AccordionButton>
            <AccordionPanel>
              <CompanyOwnerForm
                defaultValues={owner}
                isUserSwitchDisabled={true}
                maxSharePercentage={maxSharePercentage}
                onCancel={(): void => handleHideOwnerForm(owner.id)}
                onSubmit={handleCreateOwner}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ))}
      {(isOpen || owners.length === 0) && (
        <Accordion allowToggle defaultIndex={[0]} index={disableAccordion ? -1 : undefined} sx={sx}>
          <AccordionItem mt="0">
            <AccordionButton>
              <Tooltip hasArrow label={i18nOwners('Tooltip.Warning')} placement="top">
                <Icon as={ExclamationCircleIcon} boxSize="30" color="yellow.400" />
              </Tooltip>
              <Box flex="1" p="2" textAlign="left">
                <Heading fontSize="lg">{i18nOwners('NewOwnerTitle')}</Heading>
              </Box>
              <AccordionIcon boxSize="8" />
            </AccordionButton>
            <AccordionPanel>
              <CompanyOwnerForm
                isUserSwitchDisabled={!!isUserSwitchDisabled}
                maxSharePercentage={maxSharePercentage}
                onCancel={handleCancelOwnerForm}
                onSubmit={handleCreateOwner}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      <Button
        isDisabled={isOpen || owners.length === 0}
        variant="light"
        onClick={handleNewOwnerForm}
      >
        <Icon as={PlusIcon} mr="1" /> {i18nOwners('AppendShareholder')}
      </Button>
      <PreviewDocumentModal
        isOpen={previewModal.isOpen}
        src={documentPdfUrl}
        title={i18nOwners('DropzoneInputLabel')}
        onClose={previewModal.onClose}
      />
    </>
  );
}

export type CompanyOwnersProps = Props;
